import React, { useContext, FC, memo, useCallback } from 'react'
import { ScreenContext } from 'react-components'
import { Slide } from 'pure-react-carousel'
import { useTranslation } from '../../../hooks/locale'
import {
    Container,
    TitleContainer,
    ListContainer,
    SliderContainer,
    StyledCarouselProvider,
    StyledSlider,
    PopularSearchCardWrapper,
    PopularSearchCard,
    IconContainer,
    Icon,
    Label,
    Country,
} from './styles'
import { useResponsiveValue } from '../../../hooks/responsive'
import { Title } from '../common/styles'
import { getBasePath, getCarouselReachableSlideCount } from '../../../utils/miscellaneous-utils'
import PrevNextControls from '../../common/prev-next-controls'
import AnalyticsService from '../../../services/analytics-service'
import { cardEventCategories, pageTypeEventCategories } from '../../../services/analytics-service/categories'

export interface PopularSearchItem {
    id: string
    icon: string
    label: string
    country: string
    url: string
}

type Props = {
    items: PopularSearchItem[]
}

const breakpoints = [
    { query: '(max-width: 479px)', value: 2.2 },
    { query: '(min-width: 480px) and (max-width: 639px)', value: 3.2 },
    { query: '(min-width: 640px) and (max-width: 767px)', value: 4.2 },
    { query: '(min-width: 768px) and (max-width: 900px)', value: 5.2 },
    { query: '(min-width: 901px) and (max-width: 1023px)', value: 6.2 },
    { query: '(min-width: 1024px) and (max-width: 1279px)', value: 7.2 },
    { query: '(min-width: 1280px)', value: 8.2 },
]

const PopularSearchesSection: FC<Props> = ({ items }) => {
    const { isMobile } = useContext(ScreenContext)
    const { t } = useTranslation()
    const visibleSlides = useResponsiveValue(breakpoints, 2.2)
    const dragStep = Math.floor(visibleSlides || 2)
    const eventCategory = `${pageTypeEventCategories.HOME}@${cardEventCategories.sections.POPULAR_SEARCHES}`

    const handleCardClick = useCallback(
        (item: PopularSearchItem) => () => {
            AnalyticsService.trackClickEvent(eventCategory, item.url)
        },
        [eventCategory],
    )

    if (!items?.length) {
        return null
    }

    const totalSlides = getCarouselReachableSlideCount(items.length, visibleSlides)

    return (
        <Container>
            <TitleContainer>
                <Title>{t('pages.home.popularSearches.heading')}</Title>
            </TitleContainer>
            <ListContainer>
                <SliderContainer>
                    <StyledCarouselProvider
                        totalSlides={totalSlides}
                        visibleSlides={visibleSlides}
                        naturalSlideWidth={1}
                        naturalSlideHeight={1}
                        isIntrinsicHeight
                        lockOnWindowScroll
                        dragEnabled={isMobile}
                        dragStep={dragStep}
                        step={dragStep}
                    >
                        <PrevNextControls
                            totalObjects={items.length}
                            visibleSlides={visibleSlides}
                            desktopTop="-3rem"
                            desktopRight="0"
                            wideTop="-3.5rem"
                            wideRight="0"
                        />
                        <StyledSlider $transitionTime={0.2}>
                            {items.map((item, index) => (
                                <Slide index={index} key={item.id}>
                                    <PopularSearchCardWrapper>
                                        <PopularSearchCard
                                            href={item.url}
                                            noCrawl
                                            aria-label={`${item.label} holidays in ${item.country}`}
                                            onClick={handleCardClick(item)}
                                        >
                                            <IconContainer>
                                                <Icon
                                                    src={getBasePath(`${item.icon}?imwidth=56`)}
                                                    alt={`${item.label} in ${item.country}`}
                                                    width={56}
                                                    height={56}
                                                    loading="lazy"
                                                />
                                            </IconContainer>
                                            <Label>{item.label}</Label>
                                            <Country>{item.country}</Country>
                                        </PopularSearchCard>
                                    </PopularSearchCardWrapper>
                                </Slide>
                            ))}
                        </StyledSlider>
                    </StyledCarouselProvider>
                </SliderContainer>
            </ListContainer>
        </Container>
    )
}

export default memo(PopularSearchesSection)
